import { memo, useCallback, useMemo, useRef, useState } from "react";
import { isIOS, isAndroid } from "react-device-detect";
import { displayToast } from "../../components/app/AppToast";
import { Button } from "../../components/ui/Button";
import { useTranslation } from "react-i18next";
import { storeUserFingerprint } from "../../domains/user-fingerprint/endpoints/storeUserFingerprint";
import { env } from "../../config/env";
import { useLocation } from "react-router-dom";
import {
  installOriginFor,
  isAuthDoorPath,
} from "../../domains/app/appInstallWall";
import { getPostLoginRedirectPath } from "../../domains/app/utils/postLoginRedirect";
import {
  gaEventInstallWallAppClicked,
  gaEventInstallWallViewed,
  type InstallWallKind,
} from "../../tools/analytics/domains/installWall.analytics";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import { withShareCredit } from "../../tools/analytics/shareAttribution";
import {
  withFirstTouchQuery,
  withInstallWallMarker,
} from "../../tools/analytics/firstTouch";

const APP_SCHEME_IOS =
  env.appEnv === "production" ? "juisci" : "com.juisci.dev2";
const APP_SCHEME_ANDROID = `com.juisci${env.appEnv === "production" ? "" : ".dev"}`;
const APP_PACKAGE_ANDROID = `com.juisci.app${env.appEnv === "production" ? "" : ".dev"}`;
// No country segment on purpose: Apple 302s a country-less App Store URL to the
// viewer's own storefront (verified: it resolves to /us/ from a US egress, /fr/
// from a French one). Pinning `/fr/` sent every reader to the French store for
// an app that ships in eight languages. The storefront follows the Apple ID and
// the IP, never the app's language setting, and that is the right axis: it is
// what decides availability, pricing and the review locale.
const APP_STORE_IOS = "https://apps.apple.com/app/id1588725828";
const APP_STORE_ANDROID = `https://play.google.com/store/apps/details?id=${APP_PACKAGE_ANDROID}&hl=us&gl=US`;

/** Time to wait before concluding the app is not installed */
const APP_OPEN_TIMEOUT_MS = 2500;

/**
 * Detects if we're in an in-app browser (e.g. LinkedIn, Facebook, Instagram)
 * where universal links / app links don't work.
 *
 * In regular browsers (Safari, Chrome), universal links resolve before the page
 * loads — so if the user sees this page, the app is NOT installed and trying a
 * custom URL scheme would just show an error dialog.
 *
 * In in-app browsers (WKWebView), universal links are not intercepted, so the
 * user may see this page even when the app IS installed. For those cases we
 * attempt to open the app via custom URL scheme.
 */
function isInAppBrowser(): boolean {
  const ua = navigator.userAgent;

  // Known in-app browser markers
  if (
    /FBAN|FBAV|Instagram|LinkedInApp|Twitter|Line\/|Snapchat|TikTok/.test(ua)
  ) {
    return true;
  }

  // iOS: WKWebView-based in-app browsers typically lack "Safari/" in the UA,
  // while Safari, SFSafariViewController, Chrome (CriOS), Firefox (FxiOS) include it.
  if (/iPhone|iPad|iPod/.test(ua) && !/Safari\//.test(ua)) {
    return true;
  }

  // Android: WebView has "; wv)" in the UA
  if (/Android/.test(ua) && /; wv\)/.test(ua)) {
    return true;
  }

  return false;
}

/**
 * Attempts to open the app via custom URL scheme.
 * In-app browsers (LinkedIn, etc.) don't support universal/app links,
 * but custom URL schemes still work when the app is installed.
 * Resolves to true if the app opened (page became hidden), false after timeout.
 */
function tryOpenApp(schemeUrl: string): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;

    const settle = (result: boolean) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      resolve(result);
    };

    const onVisibilityChange = () => {
      if (document.hidden) settle(true);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    globalThis.location.href = schemeUrl;
    setTimeout(() => {
      settle(false);
    }, APP_OPEN_TIMEOUT_MS);
  });
}

type InstallPlatform = "ios" | "android" | "web";

function detectInstallPlatform(): InstallPlatform {
  if (isIOS) return "ios";
  if (isAndroid) return "android";

  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes("iphone") || userAgent.includes("ipad")) return "ios";
  if (userAgent.includes("android")) return "android";

  return "web";
}

/**
 * The URL the app opens after the install: the page the reader asked for.
 *
 * On a login or signup page that is not the current URL. The reader came from
 * a page that sent them there, and that page memorized itself
 * (`memorizePostLoginRedirectPath`), so the app gets that page instead of a
 * door it no longer needs. Either way the URL carries the sharer who brought
 * the reader and the browser's first touch (`withFirstTouchQuery`), which the
 * installed app has no other way to learn.
 *
 * The copy handed to the store (fingerprint, clipboard, Play referrer) also
 * carries the install-wall marker (`withInstallWallMarker`). The links that
 * open an app that is already installed do not: that app did not come from
 * the store.
 */
function getInstallDestinationUrl(): string {
  const { href, origin, pathname } = globalThis.location;
  const memorizedPath = isAuthDoorPath(pathname)
    ? getPostLoginRedirectPath()
    : "";
  return withFirstTouchQuery(
    withShareCredit(memorizedPath === "" ? href : origin + memorizedPath)
  );
}

/**
 * Whether this wall has a door.
 *
 * `none` is the front door at `/welcome`: there is no page behind it, so
 * offering "continue in the browser" would lead nowhere. `continue-in-browser`
 * is a shared paper or playlist, where the reader came for a page the API
 * serves without a session and walling them out would break the share.
 *
 * Required, with no default: a new caller has to say which audience it is
 * facing rather than inherit the gentler one by omission.
 */
export type AppInstallEscape =
  | { readonly kind: "none" }
  | { readonly kind: "continue-in-browser"; readonly onContinue: () => void };

/**
 * The tap that sends the reader to the store, or to the app when it is already
 * installed, with the page the app opens after the install. Shared by the wall
 * and the sheet below and by the offer after a logged-out JuisciGPT answer on
 * mobile web (`GPTAnonymousChatPage`), which all count in one install funnel.
 */
export function useAppInstall(wallKind: InstallWallKind): {
  isCopied: boolean;
  isInstallInProgress: boolean;
  install: (e: React.MouseEvent) => Promise<void>;
} {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const [isInstallInProgress, setInstallInProgress] = useState(false);
  const installInProgressRef = useRef(false);
  const platform = useMemo(detectInstallPlatform, []);
  const { pathname } = useLocation();
  const origin = useMemo(() => installOriginFor(pathname), [pathname]);

  // Once per session per (kind, origin): the denominator of the install funnel.
  useTrackOnMount(
    "install-wall-shown",
    {
      status: "shown",
      key: `${wallKind}:${origin.kind}:${pathname}`,
    },
    () => {
      gaEventInstallWallViewed({ kind: wallKind, origin });
    }
  );

  const copyToClipboard = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (error) {
        console.error("DDL iOS: failed to copy to clipboard", {
          error,
          text,
        });
      }
      return false;
    },
    []
  );

  const handleIOSInstall = useCallback(async (): Promise<boolean> => {
    const destinationUrl = getInstallDestinationUrl();
    // Add timestamp parameter like Branch.io does (_cpts)
    const urlWithTimestamp = withInstallWallMarker(
      `${destinationUrl}${destinationUrl.includes("?") ? "&" : "?"}_cpts=${Date.now()}`
    );

    // Fire store immediately — runs in parallel with clipboard + in-app browser check
    const storePromise = storeUserFingerprint(urlWithTimestamp).catch(
      (error) => {
        console.error(
          "DDL iOS: failed to store user fingerprint before App Store redirect",
          {
            error,
            destinationUrl,
            urlWithTimestamp,
          }
        );
      }
    );

    // Copy to clipboard first (while we still have the user gesture context)
    const success = await copyToClipboard(urlWithTimestamp);

    if (!success) {
      displayToast(t("Couldn't copy. Copy the link manually: "), "error");
    }

    // In in-app browsers (LinkedIn, etc.), universal links don't work so the user
    // may see this page even when the app is installed. Try the custom URL scheme.
    // In regular browsers (Safari), universal links already resolved — if the user
    // sees this page the app is NOT installed, so skip (avoids Safari error dialog).
    if (isInAppBrowser()) {
      const appUrl = `${APP_SCHEME_IOS}://open?url=${encodeURIComponent(destinationUrl)}`;
      const appOpened = await tryOpenApp(appUrl);
      if (appOpened) return false;
    }

    // App is not installed — show copied confirmation and redirect to App Store
    setIsCopied(true);

    // Ensure store completed before redirecting to App Store
    await storePromise;

    globalThis.location.href = APP_STORE_IOS;
    return true;
  }, [copyToClipboard, t]);

  const handleAndroidInstall = useCallback(async (): Promise<boolean> => {
    const destinationUrl = getInstallDestinationUrl();
    const urlWithTimestamp = withInstallWallMarker(
      `${destinationUrl}${destinationUrl.includes("?") ? "&" : "?"}_cpts=${Date.now()}`
    );

    await storeUserFingerprint(urlWithTimestamp).catch((error) => {
      console.error(
        "DDL Android: failed to store user fingerprint before Play Store redirect",
        {
          error,
          destinationUrl,
          urlWithTimestamp,
        }
      );
    });

    // Android intent URLs natively handle "try app → fall back to Play Store",
    // so no custom scheme attempt is needed (unlike iOS).
    const path = destinationUrl.replace(/^https?:\/\/[^/]+\//, "");
    const encodedUrl = encodeURIComponent(urlWithTimestamp);
    const playStoreUrl = `${APP_STORE_ANDROID}&referrer=${encodedUrl}`;
    const intentUrl = `intent://${path}#Intent;scheme=${APP_SCHEME_ANDROID};package=${APP_PACKAGE_ANDROID};S.browser_fallback_url=${encodeURIComponent(playStoreUrl)};end`;

    globalThis.location.href = intentUrl;
    return true;
  }, []);

  const install = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      if (installInProgressRef.current) {
        return;
      }

      installInProgressRef.current = true;
      setInstallInProgress(true);
      // Reported BEFORE the store hop, because the hop leaves the document: an
      // event queued after `location.href` is a coin flip.
      gaEventInstallWallAppClicked({ kind: wallKind, origin, platform });
      try {
        let didRedirect = false;

        if (platform === "ios") {
          didRedirect = await handleIOSInstall();
        } else if (platform === "android") {
          didRedirect = await handleAndroidInstall();
        } else {
          displayToast(
            t("Open the App Store or Play Store to get the app."),
            "info"
          );
        }

        if (!didRedirect) {
          installInProgressRef.current = false;
          setInstallInProgress(false);
        }
      } catch (error) {
        console.error("App install failed:", error);
        displayToast(t("Something went wrong. Please try again."), "error");
        installInProgressRef.current = false;
        setInstallInProgress(false);
      }
    },
    [handleAndroidInstall, handleIOSInstall, origin, platform, t, wallKind]
  );

  return { isCopied, isInstallInProgress, install };
}

function AppInstall({ escape }: { escape: AppInstallEscape }) {
  const { t } = useTranslation();
  // The escape IS the kind: a door means the reader has a page behind the ask,
  // which is what makes it a sheet rather than a wall. Deriving it here keeps
  // the three call sites from having to agree about a second prop.
  const wallKind = escape.kind === "none" ? "hard" : "sheet";
  const { isCopied, isInstallInProgress, install } = useAppInstall(wallKind);

  // The wall and the sheet are one component and must not say one thing.
  // "Install the app to continue" is TRUE at the front door and on an
  // invitation, where there is nothing behind the ask. Over a shared paper it
  // is a claim the reader can see through: the article is on screen behind the
  // sheet, so telling them they cannot proceed without installing reads as a
  // dark pattern instead of an offer, and it contradicts the door this same
  // sheet is about to show them.
  //
  // (The branch it replaces was a ternary on the platform whose two arms were
  // the same string.)
  const copy =
    escape.kind === "none"
      ? {
          title: t("Install the App to Continue"),
          body: t(
            "To proceed, you'll need to install the app. After installing, you'll be automatically redirected to continue where you left off."
          ),
        }
      : {
          title: t("Juisci is better in the app"),
          body: t(
            "Get a feed picked for your specialty, save papers, and read them offline. You can keep reading this one here."
          ),
        };

  const isInstallButtonDisabled = isInstallInProgress;

  return (
    <div className="w-full flex flex-col gap-4 p-8  min-h-[calc(50vh)] items-center justify-center">
      <div className="size-24  rounded-2xl overflow-hidden mx-auto flex items-center justify-center bg-white shadow-sm">
        <img
          src="/logo.png"
          alt="Juisci Logo"
          className="size-full  object-contain p-4"
        />
      </div>
      <h2 className="font-semibold text-2xl/8  text-primary-black m-0 text-center">
        {copy.title}
      </h2>
      <p className="font-normal text-sm/5  text-[#757b8d] m-0 text-center">
        {copy.body}
      </p>
      {isCopied ? (
        <div className="w-full p-[10px] bg-[#f5f5f5] rounded-3xl border-none font-semibold text-base/6 text-[#222234] flex  justify-center items-center gap-[10px]">
          <span className="size-5  flex items-center justify-center text-sm text-[#6ac38f]">
            ✓
          </span>
          {t("Content Copied")}
        </div>
      ) : (
        <Button
          type="button"
          variant="primary"
          size="lg"
          className="w-full"
          onClick={install}
          disabled={isInstallButtonDisabled}
          aria-busy={isInstallInProgress}
        >
          {isInstallInProgress && (
            <span
              className="size-4  border-2 border-primary-black border-t-transparent rounded-full animate-spin"
              aria-hidden="true"
            />
          )}
          {t("Continue to the Juisci App")}
        </Button>
      )}
      {escape.kind === "continue-in-browser" && (
        <button
          type="button"
          onClick={escape.onContinue}
          className="bg-transparent border-none p-0 font-medium text-sm/5 text-[#757b8d] underline cursor-pointer"
        >
          {t("Continue in browser")}
        </button>
      )}
    </div>
  );
}

export default memo(AppInstall);
